var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useFieldArray } from 'react-hook-form';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { addHttpsProtocol } from 'root/src/shared/util/protocolUtil';
import { useAddCampaignFormContext } from 'root/src/client/v2/_pages/add-campaign/contexts/addCampaignForm.context';
import { useAssetUploads } from 'root/src/client/v2/_pages/add-campaign/hooks/useAssetUploads';
import { useUploadFileMutation } from 'root/src/client/v2/file/hooks/useUploadFileMutation';
import { Dialog, DialogContent } from 'root/src/client/v2/common/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from 'root/src/client/v2/common/components/ui/popover';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Spinner } from 'root/src/client/v2/common/components/ui/spinner';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { ImageCropDialog } from 'root/src/client/v2/_pages/add-campaign/redesign/components/ImageCropDialog';
import { GoogleDrivePicker } from 'root/src/client/v2/file/redesign/components/GoogleDrivePicker';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const MAX_ASSETS = 10;
const MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB for single images (logo / thumbnail)
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.svg,.mp4';
const isVideoUrl = (url) => {
    var _a, _b;
    const path = (_a = url.split('?').at(0)) !== null && _a !== void 0 ? _a : '';
    return ((_b = path.split('.').at(-1)) !== null && _b !== void 0 ? _b : '').toLowerCase() === 'mp4';
};
// Preview url of an already-uploaded asset. Local Files are handled by useAssetPreviewUrl below, which
// owns their object-url lifecycle.
const uploadedAssetUrl = (asset) => {
    if (asset instanceof File) {
        return undefined;
    }
    if ('url' in asset && asset.url) {
        return addHttpsProtocol(asset.url);
    }
    if ('desktop' in asset && (asset.desktop || asset.mobile)) {
        return addHttpsProtocol((asset.desktop || asset.mobile));
    }
    return undefined;
};
// Preview url for any asset value. A local File gets ONE object url, created when the file appears and
// revoked when it is replaced or the tile unmounts: calling URL.createObjectURL inline during render
// leaked a blob url on every render and made the <img> reload each time.
const useAssetPreviewUrl = (asset) => {
    const [fileUrl, setFileUrl] = useState();
    useEffect(() => {
        if (!(asset instanceof File)) {
            setFileUrl(undefined);
            return undefined;
        }
        const url = URL.createObjectURL(asset);
        setFileUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [asset]);
    if (!asset) {
        return undefined;
    }
    return asset instanceof File ? fileUrl : uploadedAssetUrl(asset);
};
// Preview of a single asset inside the modal. Reserves a 16:9 area with a Skeleton so the dialog
// keeps a stable (large) size while the media loads, then fades the image / video in.
const AssetPreview = ({ url }) => {
    const [loaded, setLoaded] = useState(false);
    const video = isVideoUrl(url);
    return (<div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black/30">
			{!loaded && <Skeleton className="absolute inset-0 rounded-lg"/>}
			{video ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={url} controls autoPlay onLoadedData={() => setLoaded(true)} className={cn('absolute inset-0 h-full w-full object-contain transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')}/>) : (<Img src={url} alt="Asset preview" onLoad={() => setLoaded(true)} className={cn('absolute inset-0 h-full w-full object-contain transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')}/>)}
		</div>);
};
// One asset tile: drag handle (whole tile, dnd-kit) + click-to-preview + a remove button. dnd-kit's
// distance activation means a plain click previews while a drag reorders.
const SortableAssetTile = ({ sortId, asset, isUploading, isError, progress, onRemove, onRetry, onPreview }) => {
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: sortId });
    const assetUrl = useAssetPreviewUrl(asset);
    const preview = isUploading ? undefined : assetUrl;
    const canPreview = Boolean(preview) && !isError;
    return (<div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }} {...attributes} {...listeners} onClick={() => { if (canPreview && preview)
        onPreview(preview); }} className={cn('group relative h-20 w-20 overflow-hidden rounded-xl border border-solid bg-secondary', isError ? 'border-destructive/60' : 'border-white/[0.08]', canPreview ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing')}>
			{preview && !isVideoUrl(preview) && <Img src={preview} alt="Asset" className="h-full w-full object-cover"/>}
			{preview && isVideoUrl(preview) && (<div className="flex h-full w-full items-center justify-center bg-black/40">
					<PlayArrowIcon sx={{ fontSize: 26 }} className="text-white"/>
				</div>)}
			{isUploading && (<div className="flex h-full w-full items-center justify-center bg-white/[0.03] text-[11px] font-bold text-muted-foreground">
					{Math.round(progress * 100)}%
				</div>)}
			{isError && (<button type="button" onClick={(event) => { event.stopPropagation(); onRetry(); }} onPointerDown={event => event.stopPropagation()} className="flex h-full w-full flex-col items-center justify-center gap-1 bg-destructive/10 text-[10px] text-destructive">
					<ReplayIcon sx={{ fontSize: 16 }}/>
					Retry
				</button>)}
			<button type="button" onClick={(event) => { event.stopPropagation(); onRemove(); }} onPointerDown={event => event.stopPropagation()} className={cn('absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-solid border-white/15', 'bg-black/55 text-white/90 opacity-0 backdrop-blur transition-all hover:bg-destructive hover:text-white group-hover:opacity-100')} aria-label="Remove asset">
				<CloseIcon sx={{ fontSize: 14 }}/>
			</button>
		</div>);
};
// Multi-asset uploader (campaign media). Mirrors the legacy AssetsField integration: append the
// File so the slot exists, upload via `useAssetUploads` (shares the asset-upload mutation key the
// auto-save watches), and `update(slotIndex, data)` on success. Supports drag-reorder (dnd-kit) and
// click-to-preview.
export const AssetUploader = () => {
    var _a;
    const form = useAddCampaignFormContext();
    const { fields, append, update, remove, move } = useFieldArray({ control: form.control, name: 'assets' });
    // The live values, not the field-array snapshot: `fields` spreads each entry, which turns a File
    // into an empty object, so it can neither preview a pending upload nor identify its slot. Read
    // straight from the form rather than through useWatch, whose copy only catches up in the field
    // array's effect - one frame in which it is still the pre-remove / pre-reorder array while `fields`
    // has already moved on, so every tile would render its neighbour's image. Every writer here
    // (append / update / remove / move, and the autofill's setValue on this array name) re-renders this
    // component through useFieldArray, so a plain read stays current.
    const assetValues = ((_a = form.getValues('assets')) !== null && _a !== void 0 ? _a : []);
    // Resolve the slot from the File itself at completion time. The index the upload started at is
    // stale as soon as the user removes or reorders an asset mid-upload, and writing to it overwrote a
    // different asset (or, past the end of the array, punched an `undefined` hole into `assets` that
    // then went out in the save payload). A file that is gone by the time its upload lands was removed
    // on purpose, so the result is simply dropped.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onUploadSuccess = useCallback((_slotIndex, data, file) => {
        var _a;
        const currentIndex = ((_a = form.getValues('assets')) !== null && _a !== void 0 ? _a : []).findIndex(asset => asset === file);
        if (currentIndex === -1) {
            return;
        }
        update(currentIndex, data);
    }, [form, update]);
    const assetUploads = useAssetUploads({ onSuccess: onUploadSuccess });
    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }), useSensor(TouchSensor));
    const [inputKey, setInputKey] = useState(0);
    const [isDropOver, setIsDropOver] = useState(false);
    const [preview, setPreview] = useState(null);
    const isDisabled = form.formState.isSubmitting || form.formState.isLoading;
    const handleFiles = useCallback((files) => {
        const remaining = MAX_ASSETS - fields.length;
        if (remaining <= 0) {
            return;
        }
        const validFiles = assetUploads.validateAndFilterFiles(files, remaining);
        if (!validFiles.length) {
            return;
        }
        const startIndex = fields.length;
        for (const file of validFiles) {
            append(file);
        }
        assetUploads.uploadFiles(validFiles, startIndex);
    }, [fields.length, assetUploads, append]);
    const onSelect = (event) => {
        const fileList = event.target.files;
        if (fileList === null || fileList === void 0 ? void 0 : fileList.length) {
            handleFiles(Array.from(fileList));
        }
        setInputKey(prev => prev + 1);
    };
    const onDrop = (event) => {
        event.preventDefault();
        setIsDropOver(false);
        if (isDisabled) {
            return;
        }
        const files = Array.from(event.dataTransfer.files);
        if (files.length) {
            handleFiles(files);
        }
    };
    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const from = Number(active.id);
        const to = Number(over.id);
        if (Number.isNaN(from) || Number.isNaN(to)) {
            return;
        }
        move(from, to);
    };
    const hasRoom = fields.length < MAX_ASSETS;
    return (<div className="space-y-3">
			{fields.length > 0 && (<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
					<SortableContext items={fields.map((_, index) => String(index))} strategy={rectSortingStrategy}>
						<div className="flex flex-wrap gap-3">
							{fields.map((field, index) => {
                var _a;
                const asset = assetValues[index];
                const uploadState = assetUploads.getFileState(asset);
                return (<SortableAssetTile key={field.id} sortId={String(index)} asset={asset} isUploading={(uploadState === null || uploadState === void 0 ? void 0 : uploadState.status) === 'uploading'} isError={(uploadState === null || uploadState === void 0 ? void 0 : uploadState.status) === 'error'} progress={(_a = uploadState === null || uploadState === void 0 ? void 0 : uploadState.progress) !== null && _a !== void 0 ? _a : 0} onRemove={() => {
                        if (uploadState) {
                            assetUploads.dismissUpload(uploadState.id);
                        }
                        remove(index);
                    }} onRetry={() => uploadState && assetUploads.retryUpload(uploadState.id)} onPreview={url => setPreview(url)}/>);
            })}
						</div>
					</SortableContext>
				</DndContext>)}

			{hasRoom && (<label htmlFor="redesign-asset-upload" onDragOver={(event) => { event.preventDefault(); setIsDropOver(true); }} onDragLeave={() => setIsDropOver(false)} onDrop={onDrop} className={cn('flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-colors', isDropOver ? 'border-primary/40 bg-primary/5' : 'border-white/[0.1] bg-white/[0.02] hover:border-primary/30', isDisabled && 'pointer-events-none opacity-50')}>
					<CloudUploadIcon sx={{ fontSize: 28 }} className="text-muted-foreground"/>
					<span className="text-sm text-muted-foreground">Drop files here or click to browse</span>
					<span className="text-[11px] text-muted-foreground/60">Max 100MB per file. JPG, PNG, SVG, MP4</span>
				</label>)}
			{hasRoom && (<GoogleDrivePicker multiple disabled={isDisabled} onChange={files => handleFiles(files)}/>)}
			<input id="redesign-asset-upload" key={inputKey} type="file" multiple accept={ACCEPTED_EXTENSIONS} className="hidden" onChange={onSelect} disabled={isDisabled}/>
			<p className="text-[11px] text-muted-foreground/60">{fields.length}/{MAX_ASSETS} assets. Drag to reorder, click to preview.</p>

			<Dialog open={Boolean(preview)} onOpenChange={(open) => { if (!open)
        setPreview(null); }}>
				<DialogContent className="max-w-3xl border-white/[0.08] bg-card/80 p-3 backdrop-blur-xl">
					{preview && <AssetPreview key={preview} url={preview}/>}
				</DialogContent>
			</Dialog>
		</div>);
};
// Single-image uploader (brand logo, multi-game thumbnail). Selecting a file opens the redesign
// crop modal (`ImageCropDialog`); the cropped File is then uploaded via `useUploadFileMutation`.
export const ImageUploader = ({ value, onUploaded, onClear, className, error, aspect = 1 }) => {
    const upload = useUploadFileMutation();
    const inputRef = useRef(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [cropSrc, setCropSrc] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const display = value ? addHttpsProtocol(value) : '';
    const openCropWithFile = (file) => {
        if (!file) {
            return;
        }
        if (file.type && !file.type.startsWith('image/')) {
            toast.error('Please choose an image file (JPG, PNG or SVG)');
            return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
            toast.error('Image is too large, maximum size is 15MB');
            return;
        }
        const url = URL.createObjectURL(file);
        setCropSrc((prev) => {
            if (prev) {
                URL.revokeObjectURL(prev);
            }
            return url;
        });
    };
    const onSelect = (event) => {
        var _a;
        const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        openCropWithFile(file);
    };
    const onDrop = (event) => {
        var _a;
        event.preventDefault();
        setDragOver(false);
        openCropWithFile((_a = event.dataTransfer.files) === null || _a === void 0 ? void 0 : _a[0]);
    };
    const closeCrop = () => {
        setCropSrc((prev) => {
            if (prev) {
                URL.revokeObjectURL(prev);
            }
            return null;
        });
    };
    const onCropped = (file) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield upload.mutateAsync(file);
        onUploaded(result);
    });
    return (<div className={className}>
			{display ? (<div className="group relative h-40 w-full overflow-hidden rounded-xl border border-solid border-white/[0.08] bg-white/[0.02]">
					<Img src={display} alt="Upload preview" className="h-full w-full object-contain p-4"/>
					<div className={cn('absolute inset-0 flex items-center justify-center gap-2 bg-black/55 opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100', confirmRemove && 'opacity-100')}>
						<Button type="button" variant="secondary" size="sm" loading={upload.isPending} onClick={() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }}>
							<CloudUploadIcon sx={{ fontSize: 15 }}/>
							Change
						</Button>
						{onClear && (<Popover open={confirmRemove} onOpenChange={setConfirmRemove}>
								<PopoverTrigger asChild>
									<Button type="button" variant="destructive" size="sm">Remove</Button>
								</PopoverTrigger>
								<PopoverContent align="center" className="w-56">
									<p className="mb-3 text-xs text-foreground">Remove this image?</p>
									<div className="flex justify-end gap-2">
										<Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => setConfirmRemove(false)}>Cancel</Button>
										<Button type="button" variant="destructive" size="sm" className="h-8" onClick={() => { onClear(); setConfirmRemove(false); }}>Remove</Button>
									</div>
								</PopoverContent>
							</Popover>)}
					</div>
				</div>) : (<>
					<button type="button" onClick={() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }} onDragOver={(event) => { event.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={cn('flex h-40 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-colors', dragOver ? 'border-primary/40 bg-primary/5' : error ? 'border-destructive/60' : 'border-white/[0.1] bg-white/[0.02] hover:border-primary/30')}>
						{upload.isPending ? (<Spinner size={24} className="text-muted-foreground"/>) : (<CloudUploadIcon sx={{ fontSize: 28 }} className="text-muted-foreground"/>)}
						<span className="text-sm text-muted-foreground">Drop your logo here or click to browse</span>
						<span className="text-[11px] text-muted-foreground/60">PNG or SVG, square recommended</span>
					</button>
					<div className="mt-2">
						<GoogleDrivePicker disabled={upload.isPending} onChange={files => openCropWithFile(files[0])}/>
					</div>
				</>)}
			<input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.svg" className="hidden" onChange={onSelect}/>
			{cropSrc && (<ImageCropDialog open={Boolean(cropSrc)} src={cropSrc} aspect={aspect} onOpenChange={(open) => { if (!open)
            closeCrop(); }} onCropped={onCropped}/>)}
		</div>);
};
